import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Tooltip, message, Popover, Switch } from 'antd';
import {
  TabMenuContainer,
  SettingsHeading,
  FontFamilyInput,
  SizeButton,
  SizeContainer,
  DisplayNameInput,
} from '../../SidebarMenu/styles';
import { DESIGN_AREA_SETTINGS_TAB } from '../../SidebarMenu/translationValues';
import AccessibilitySummaryEditor from '../../SidebarMenu/AccessibilitySummaryEditor';
import FileSettings from './FileSettings';
import { DebounceInput } from '../../../components/DebounceInput';
import { changePpi, updateAccessible, updateCloneable, updateDisplayName, updateRenderingType } from '../../../utils/services/complexDocumentsAPI';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import {
  CONTEXT_KEYS,
  TEMPLATE_TYPE,
  useArtboardContext,
} from '../../../context/complexArtboardContext';
import { deepCopy } from '../../../utils/javascript';
import { COMPLEX_DOCUMENT_VERSION } from '../../../utils/complexDocumentConstants';
import _ from 'underscore';
import FontSideBar from '../FontSideBar';
import { ButtonGroup } from 'react-rainbow-components';
import { fontSizes } from '../TextBlockToolbar';
import { useDefaultStyleContext }  from '../../../context/complexDefaultStyleContext';
import { ENTERPRISE_ENV } from '../../../utils/helper';
import QuillEditorModal from '../../../components/Modals/QuillEditorModal.js';
import { convertContractToReact } from '../../../utils/TextBlockHelper';
import PdfQualitySelection from './PdfQualitySelection.js';
import CustomToolBar from '../complexVariables/CustomToolBar.js';
import ReactQuill, { Quill } from 'react-quill';
import { modules } from '../complexVariables/CustomToolBar.js';
import { checkSessionUserAccess } from '../../../utils/helper';
import { useSessionContext } from '../../../context/sessionContext.js';
import { TemplateDescription } from '../../SidebarMenu/styles';
import { RichTextEditorContainer } from '../complexVariables/styles.js';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { saveTemplateDescription } from '../../../utils/services/complexDocumentsAPI';

const blockAndFlowTypes = [TEMPLATE_TYPE.BLOCK, TEMPLATE_TYPE.FLOW];

const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

const DEFAULT_VALUE = [
  {
    contentType: 'PARAGRAPH',
    content: ''
  },
  {
    contentType: "LINE_BREAK",
    content: "\n"
  }
];

const RENDERING_TYPES = {
  DIGITAL: "digital",
  PRINT_RGB: "print_rgb",
  PRINT_CMYK: "print_cmyk",
};

const renderingTypeMap = {
  0: RENDERING_TYPES.DIGITAL,
  1: RENDERING_TYPES.PRINT_RGB,
  2: RENDERING_TYPES.PRINT_CMYK,
};

const ComplexSettingsTab = ({ CurrentDesign, fontListVisible, onFontToogle, ...props }) => {
  const { t } = useTranslation(NAMESPACES);

  const customSelector = useCallback(
    store => {
      const templateId = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
      let ppi = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]?.ppi;
      if (CurrentDesign === TEMPLATE_TYPE.FLOW) {
        const flow = store[store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0]];
        ppi = flow?.ppi;
      } else if (CurrentDesign === TEMPLATE_TYPE.BLOCK) {
        const flow = store[store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0]];
        const pageId = flow?.pages?.[0];
        const blockUUIDs = flow[pageId];
        if (blockUUIDs?.length) {
          for (let i = 0; i < blockUUIDs.length; i++) {
            const block = store[blockUUIDs[i]];
            if (block?.style?.ppi) {
              ppi = block.style.ppi;
              break;
            }
          }
        }
      }
      return {
        resourceId: templateId,
        ppi: ppi,
      };
    },
    [CurrentDesign],
  );
  const [{ resourceId, ppi }, store] = useDocumentContext(customSelector);


  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, ``);

  const lifecycleSelector = useCallback(store => store[CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateArtboard] = useArtboardContext(lifecycleSelector);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [defaultStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const designSummarySelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY], []);
  const [summary] = useDocumentContext(designSummarySelector, ``);

  const designEditableSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EDITABLE], []);
  const [isEditable] = useDocumentContext(designEditableSelector, `ComplexSettingsTab`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexSettingsTab`);

  const isReadOnlySelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [isEditMode] = useArtboardContext(isReadOnlySelector, `ComplexSettingsTab`);

  const [, methods] = useDocumentContext(()=>null, 'ComlpexSettingsTab');

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(customVarSelector, 'ComplexSettingsTab');

  const displayNameSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME], []);
  const [displayNameDTO] = useDocumentContext(displayNameSelector, 'ComplexSettingsTab');

  const renderingTypeSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.RENDERING_TYPE], []);
  const [renderingType] = useDocumentContext(renderingTypeSelector, `ComplexSettingsTab`);

  const accessibilitySelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_ACCESSIBLE], []);
  const [accessibilityEnabled] = useDocumentContext(accessibilitySelector, `ComplexSettingsTab`);

  const isOpenDesignSelector = useCallback((store) => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign] = useArtboardContext(isOpenDesignSelector, `ComplexSettingsTab`);

  const templateDescriptionSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_DESCRIPTION], []);
  const [templateDescription] = useDocumentContext(templateDescriptionSelector, `ComplexSettingsTab`);

  const inputRef = useRef();
  const editorRef = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [isCloneable, setIsCloneable] = useState(isEditable);
  const [pdfQuality, setPdfQuality] = useState(renderingType);
  const [isAccessible, setIsAccessible] = useState(accessibilityEnabled);
  const [displayNameModalVisible, setDisplayNameModalVisible] = useState(false);
  const [html, setHtml] = useState('');
  const [defaultValue, setDefaultValue] = useState(templateDescription);
  const [isdescriptionSaved, setIsdescriptionSaved] = useState(false);
  const [keyValue, setKeyValue] = useState(0);

  useEffect(()=>{
     setKeyValue(prev => prev + 1);
  },[])

  const onSizeChange = newFontSize => {
    if (!isNaN(newFontSize)) {
      methods.updatefontFamilyAndSize(CurrentDesign, newFontSize, defaultStyle.fontSize, 'fontSize', 'update' );
    }
  };

  const changeFontFamily = newFontFamily => {
    methods.updatefontFamilyAndSize(CurrentDesign, newFontFamily, defaultStyle.fontFamily, 'fontFamily', 'update' );
  };

  const handleKeypress = e => {
    e.stopPropagation();
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const value = parseFloat(inputRef.current.innerHTML.trim());
      if (!isNaN(value) && value >= 1 && value <= 400) {
        onSizeChange(value);
      }
    }
  };

  const renderSize = () => {
    return (
      <span>
        <ButtonGroup className='fontSize-button'>
          <SizeButton className='fontSize-button'
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize - 1);
              }
            }}
            radiusLeft={true}
            icon="minus"
          />
          <Popover
            content={
              <SizeContainer>
                <ul>
                  {fontSizes.map(data => (
                    <li
                      key={data}
                      onClick={e => {
                        onSizeChange(parseInt(data?.replaceAll('px', '')) );
                      }}
                    >
                      {data}
                    </li>
                  ))}
                </ul>
              </SizeContainer>
            }
            title={null}
            trigger="click"
          >
            <div
              ref={inputRef}
              contentEditable={true}
              suppressContentEditableWarning={true}
              className="size-ref ql-size"
              style={{
                width: '50px',
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#cdcdcd',
              }}
              onKeyPress={handleKeypress}
              onBlur={e => {
                const value = parseFloat(inputRef.current.innerHTML.trim());
                if (value !== defaultStyle.fontSize && !isNaN(value) && value >= 1 && value <= 400) {
                  onSizeChange(value);
                }
              }}
            >
              {!isNaN(defaultStyle.fontSize) && defaultStyle.fontSize ? defaultStyle.fontSize : ' '}
            </div>
          </Popover>
          <SizeButton className='fontSize-button'
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize + 1);
              }
            }}
            radiusLeft={false}
            icon="plus"
          />
        </ButtonGroup>
      </span>
    );
  };


  const onPpiChange = async value => {
    const payload = {
      ppi: value,
      resourceId: resourceId,
      resourceType: CurrentDesign,
    };
    if (blockAndFlowTypes.includes(CurrentDesign)) {
      payload.isStandalone = true;
    }
    try {
      const response = await changePpi(payload);
      if (response?.statusCode === 1930) {
        const currentStore = store.get();
        if (CurrentDesign === TEMPLATE_TYPE.DOCUMENT) {
          const style = deepCopy(currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]);
          style.ppi = value;
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]: style });
        } else if (CurrentDesign === TEMPLATE_TYPE.FLOW) {
          const flowUUID = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0];
          const flow = { ...deepCopy(currentStore[flowUUID]), ppi: value };
          store.set({ [flowUUID]: flow });
        } else if (CurrentDesign === TEMPLATE_TYPE.BLOCK) {
          const flow = currentStore[currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0]];
          const pageId = flow?.pages?.[0];
          const blockUUIDs = flow[pageId];
          const storeObj = {};
          blockUUIDs.forEach(blockUUID => {
            const block = deepCopy(currentStore[blockUUID]);
            if (block?.style) {
              block.style.ppi = value;
            }
            storeObj[blockUUID] = block;
          });
          store.set(storeObj);
        }
        updateArtboard({
          [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: {
            ...lifeCycle,
            [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date(),
          },
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      message.error(t(DESIGN_AREA_SETTINGS_TAB.PPI_UPDATE_ERROR.key, DESIGN_AREA_SETTINGS_TAB.PPI_UPDATE_ERROR.value));
    }
  };

  const saveDisplayName = useCallback(
    async data => {
      try {
        setIsLoading(true);
        const payload = {};
        payload.documentId = documentId;
        payload.displayNameBlockDTO = {
          blockType: 'text',
          contents: data.contents,
          variableContract: data.variableContract,
          orderNumber: 1,
          isDeleted: false,
          isEditable: false,
          isStandalone: false,
          blockDTOList: [],
          isTranslated: false,
          style: {
            accessible: true,
          },
        };
        const response = await updateDisplayName(documentId, payload);
        if (response?.statusCode === 2001) {
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME]: payload.displayNameBlockDTO });
          setDisplayNameModalVisible(false);
        } else {
          message.error(
            t(
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UPDATE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UPDATE_FAIL_ERROR_MESSAGE.value,
            ),
          );
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    },
    [documentId],
  );

  const onDocumentSummarySave = useCallback(({contents, newGlobalVariables}) => {
    methods.updateSummary(contents, newGlobalVariables);
  }, []);

  const onCloneableChange = useCallback(
    async check => {
      if (isOpenDesign) {
        handleOpenDesign()
        return;
      }
      setIsCloneable(check);
      const showErrorMessage = () => {
        if (check) {
          return message.error(
            t(
              DESIGN_AREA_SETTINGS_TAB.EDITABLE_ENABLE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.EDITABLE_ENABLE_FAIL_ERROR_MESSAGE.value,
            )
          );
        }
        return message.error(
          t(
              DESIGN_AREA_SETTINGS_TAB.EDITABLE_DISABLE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.EDITABLE_DISABLE_FAIL_ERROR_MESSAGE.value,
            )
        );
      };
      try {
        const response = await updateCloneable(documentId, check);
        if (response?.statusCode === 5406) {
          store.set({
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EDITABLE]: check,
          });
        } else {
          setIsCloneable(!check);
          showErrorMessage();
        }
      } catch (e) {
        setIsCloneable(!check);
        showErrorMessage();
      }
    },
    [documentId],
  );
  
  const onPdfQualityChange = useCallback(
    async value => {
      if (isOpenDesign) {
        handleOpenDesign()
        return;
      }
      const showErrorMessage = () => {
        if (value) {
          return message.error(
            t(
              DESIGN_AREA_SETTINGS_TAB.PDF_QUALITY_CHANGE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.PDF_QUALITY_CHANGE_FAIL_ERROR_MESSAGE.value,
            )
          );
        }
      };
      try {
        const response = await updateRenderingType(documentId, renderingTypeMap[value]);
        if (response?.statusCode === 847) {
          store.set({
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.RENDERING_TYPE]: value,
          });
          setPdfQuality(value);
          if (value === 1 || value === 2) {
            setIsAccessible(false);
            store.set({
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_ACCESSIBLE]: false,
          });
          }
        } else {
          showErrorMessage();
        }
      } catch (e) {
        showErrorMessage();
      }
    },
    [documentId],
  );

  const onAccessibleChange = useCallback(
    async check => {
      if (isOpenDesign) {
        handleOpenDesign()
        return;
      }
      setIsAccessible(check);
      const showErrorMessage = () => {
        if (check) {
          return message.error(
            t(
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_ENABLE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_ENABLE_FAIL_ERROR_MESSAGE.value,
            )
          );
        }
        return message.error(
          t(
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_DISABLE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_DISABLE_FAIL_ERROR_MESSAGE.value,
            )
        );
      };
      try {
        const response = await updateAccessible(documentId, check);
        if (response?.statusCode === 849) {
          store.set({
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_ACCESSIBLE]: check,
          });
        } else {
          setIsAccessible(!check);
          showErrorMessage();
        }
      } catch (e) {
        setIsAccessible(!check);
        showErrorMessage();
      }
    },
    [documentId],
  );

  function handleOpenDesign() {
    updateArtboard({
          [CONTEXT_KEYS.SHOW_SIGNUP_PROMPT]: true
    });
  }
  const quillContentChangeHandler = (html, delta, source, editor) => {
    const quillContents = editor.getContents()
    const converter = new QuillDeltaToHtmlConverter(quillContents.ops, {
      customCssClasses: {
        'ql-align-right': 'text-align: right;',
      },
      inlineStyles: true
    });
    const convertedHtml = converter.convert();
    setHtml(convertedHtml);
  }

  const onSave = async () => {
    setIsdescriptionSaved(false);
    const payload = {
      documentId: documentId,
      description: html
    }
    try {
      const response = await saveTemplateDescription(payload);
      setIsdescriptionSaved(true);
      if (response?.statusCode === 5500) {
        store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_DESCRIPTION]:html});
        setDefaultValue(html);
        message.success(
          t(DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVED.key, DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVED.value)
        );
      }
      else {
        message.error(
          t(DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.key, DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.value)
        );
      }
    }
    catch {
      message.error(
        t(DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.key, DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.value)
      );
    }
  }
  return (
    <TabMenuContainer complexDesign disabled={isInteractiveDesign || !isEditMode}>
      {CurrentDesign === 'document' && (
        <>
          <SettingsHeading complexDesign>
            {t(
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_LABEL.key,
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_LABEL.value,
            )}{' '}
            <Tooltip
              placement="topLeft"
              title={t(
                DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_TOOLTIP.key,
                DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_TOOLTIP.value,
              )}
            >
              <Icon type="question-circle" />
            </Tooltip>
          </SettingsHeading>
          <DisplayNameInput onClick={() => isOpenDesign ? handleOpenDesign() : setDisplayNameModalVisible(true)}>
          <div className='quill'>
            <div className='ql-container ql-snow ql-preview'>
              {convertContractToReact(displayNameDTO?.contents || DEFAULT_VALUE, undefined, undefined, globalVariables)}
            </div>
          </div>
            <Icon
              type="edit"
              style={{ color: '#000', cursor: 'pointer', fontSize: '18px' }}
              onClick={() => isOpenDesign ? handleOpenDesign() : setDisplayNameModalVisible(true)}
            />
          </DisplayNameInput>
        </>
      )}
      <FileSettings />
      
      <SettingsHeading complexDesign>
        {t(DESIGN_AREA_SETTINGS_TAB.PPI_LABEL.key, DESIGN_AREA_SETTINGS_TAB.PPI_LABEL.value)}
      </SettingsHeading>
      <DebounceInput
        defaultValue={ppi || 300}
        className="manage-setting-input"
        onChange={onPpiChange}
        onClick={isOpenDesign && handleOpenDesign}
        readOnly={isOpenDesign}
      />
      <div className='doc-settings-grouping settings-heading'>
        <SettingsHeading complexDesign className='doc-settings-width'>
          {t(DESIGN_AREA_SETTINGS_TAB.FONT_FAMILY.key, DESIGN_AREA_SETTINGS_TAB.FONT_FAMILY.value)}
        </SettingsHeading>
        <SettingsHeading complexDesign className='font-size-style'>
          {t(DESIGN_AREA_SETTINGS_TAB.FONT_SIZE.key, DESIGN_AREA_SETTINGS_TAB.FONT_SIZE.value)}
        </SettingsHeading>
      </div>

      <div className='doc-settings-grouping'>
        <FontFamilyInput complexDocumentDesign className='doc-settings-width'>
          <div onClick={onFontToogle} className="input">
            <span>{defaultStyle.fontFamily}</span>
          </div>
        </FontFamilyInput>
        <div className='font-size-style'>{renderSize()}</div>
      </div>


        {fontListVisible && (
          <FontSideBar
            itemUUID={undefined}
            editor={undefined}
            setFontChange={changeFontFamily}
            fontFamily={defaultStyle.fontFamily}
            isToolBarVisible={fontListVisible}
            closeFontList={onFontToogle}
            isAbsolute={true}
            CurrentDesign={CurrentDesign}
          />
        )}
      {!checkSessionUserAccess(userDetails, 'master') && CurrentDesign === 'document' &&  <SettingsHeading complexDesign>
        {t(
          DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_LABEL.key,
          DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_LABEL.value,
        )}{' '}
        <Tooltip
          placement="topLeft"
          title={t(
            DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_TOOLTIP.key,
            DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_TOOLTIP.value,
          )}
        >
          <Icon type="question-circle" />
        </Tooltip>
        <Switch checked={isAccessible} onChange={onAccessibleChange} />
      </SettingsHeading>}

      {checkSessionUserAccess(userDetails, 'master') &&
        <>
          <SettingsHeading complexDesign>
            {t(DESIGN_AREA_SETTINGS_TAB.TEMPLATE_DESCRIPTION_LABEL.key, DESIGN_AREA_SETTINGS_TAB.TEMPLATE_DESCRIPTION_LABEL.value)}{' '}
          </SettingsHeading>
          <RenderTemplateDescription t={t} defaultValue={defaultValue} quillContentChangeHandler={quillContentChangeHandler} onSave={onSave} editorRef={editorRef} isdescriptionSaved={isdescriptionSaved} keyValue={keyValue} />
        </>
      }
      {CurrentDesign === 'document' && (
        <>
        <SettingsHeading complexDesign>
          {t(DESIGN_AREA_SETTINGS_TAB.DOCUMENT_SUMMARY_LABEL.key, DESIGN_AREA_SETTINGS_TAB.DOCUMENT_SUMMARY_LABEL.value)}{' '}
        <Tooltip
          placement="topLeft"
          title={t(DESIGN_AREA_SETTINGS_TAB.DOCUMENT_SUMMARY_TOOLTIP.key, DESIGN_AREA_SETTINGS_TAB.DOCUMENT_SUMMARY_TOOLTIP.value)}
        >
          <Icon type="question-circle" />
        </Tooltip>
      </SettingsHeading>
      <AccessibilitySummaryEditor
        itemUUID={'document-summary'}
        contents={summary?.contents}
        customContainerStyle={{ marginTop: 10 }}
        onSaveChanges={onDocumentSummarySave}
        customizeHistory={false}
        customClassName={'summary-editor'}
      />
      {ENTERPRISE_ENV && CurrentDesign === "document" && !isInteractiveDesign ? (
        <SettingsHeading complexDesign cloneable>
          {t(
            DESIGN_AREA_SETTINGS_TAB.CLONEABLE_LABEL.key,
            DESIGN_AREA_SETTINGS_TAB.CLONEABLE_LABEL.value,
          )}{' '}
          <Tooltip
            placement="topLeft"
            title={t(
              DESIGN_AREA_SETTINGS_TAB.CLONEABLE_TOOLTIP.key,
              DESIGN_AREA_SETTINGS_TAB.CLONEABLE_TOOLTIP.value,
            )}
          >
            <Icon type="question-circle" />
          </Tooltip>
          <Switch checked={isCloneable} onChange={onCloneableChange} />
        </SettingsHeading>
      ) : null}
       {!checkSessionUserAccess(userDetails, 'master') && <SettingsHeading complexDesign>
          {t(
            DESIGN_AREA_SETTINGS_TAB.PDF_QUALITY_LABEL.key,
            DESIGN_AREA_SETTINGS_TAB.PDF_QUALITY_LABEL.value,
          )}{' '}
          <Tooltip
            placement="topLeft"
            title={t(
              DESIGN_AREA_SETTINGS_TAB.PDF_QUALITY_TOOLTIP.key,
              DESIGN_AREA_SETTINGS_TAB.PDF_QUALITY_TOOLTIP.value,
            )}
          >
            <Icon type="question-circle" />
          </Tooltip>
          <PdfQualitySelection selectedQuality={pdfQuality} onChange={onPdfQualityChange}/>
        </SettingsHeading>}
    
      </>
    )}

    <QuillEditorModal 
      visible={displayNameModalVisible}
      value={displayNameDTO?.contents || DEFAULT_VALUE}
      isLoading={isLoading}
      onSave={saveDisplayName}
      onCancel={() => setDisplayNameModalVisible(false)}
      inputLabel={t(DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_MODAL_LABEL.key, DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_MODAL_LABEL.value)}
    />
    </TabMenuContainer>
  );
};

const RenderTemplateDescription = ({ t, defaultValue, quillContentChangeHandler, onSave, editorRef, isdescriptionSaved, keyValue }) => {
  return (
      <RichTextEditorContainer isTemplateDescription={true}>
        {keyValue ? <div key={keyValue} style={{ marginTop: '6px', border: '2px solid white', transition: 'opacity 0.9s ease-in-out', borderRadius: '4px', background: 'white', borderRadius: '10px', padding: '2px' }}>
          <CustomToolBar t={t} isTemplateDescription={true} className='custom-picker' varName={'html-ixck'} onSave={onSave} editorRef={editorRef} isdescriptionSaved={isdescriptionSaved} />
          <ReactQuill style={{ backgroundColor: 'white' }}
            className='custom-picker'
            ref={editorRef}
            defaultValue={defaultValue}
            modules={modules('html-ixck')}
            onChange={(html, delta, source, editor) => { quillContentChangeHandler(html, delta, source, editor) }}
          />
        </div> : null}
      </RichTextEditorContainer>
  )
}

export default ComplexSettingsTab;


